import dayjs from 'dayjs';
import _ from 'lodash';

import { COMMON_REQUEST_DATE_FORMAT } from 'constants/date';
import { BaseStatus } from 'constants/loanApplication';
import { getConditionalObject } from 'utils/data';

import {
  CREDIT_EVENT_DATES_OVERRIDDEN_KEY,
  CREDIT_EVENT_FORM_KEYS,
  CREDIT_EVENT_GROUPS,
  CREDIT_REPORT_FINANCES_KEYS,
  CreditEventDateKey,
} from './Finances.constants';

const checkIsYes = value => value === true || value === BaseStatus.YES;

const toRequestDate = value =>
  value && dayjs(value).isValid() ? dayjs(value).format(COMMON_REQUEST_DATE_FORMAT) : null;

/**
 * The loan officer's event dates shown for a borrower: an event's dates show when its declaration
 * is Yes, or when the credit report carries that event (it can contradict a No).
 */
export const getVisibleCreditEventDateKeys = ({ finances, storedFinances }) =>
  CREDIT_EVENT_GROUPS.flatMap(({ declarationKey, dateKeys, creditDateKeys }) =>
    checkIsYes(finances?.[declarationKey]) ||
    creditDateKeys.some(key => !_.isNil(storedFinances?.[key]))
      ? dateKeys
      : [],
  );

const hasStoredCreditEventDates = storedFinances =>
  Object.values(CreditEventDateKey).some(key => !_.isNil(storedFinances?.[key])) ||
  storedFinances?.[CREDIT_EVENT_DATES_OVERRIDDEN_KEY] === true;

const hasFinancesDeclarations = finances =>
  CREDIT_EVENT_GROUPS.some(({ declarationKey }) => _.has(finances, declarationKey));

/**
 * The borrower patch's creditEventDates group, a full save of the six dates (a hidden or blank date
 * clears it) plus the override flag (null keeps the stored one; false once no event dates are shown).
 * Null when the Finances section never placed these fields in the form and the borrower has no stored
 * dates to clear, so the stored dates are left untouched.
 */
export const getCreditEventDatesToSend = ({ finances, storedFinances }) => {
  const hasDateFields = CREDIT_EVENT_FORM_KEYS.some(key => _.has(finances, key));
  // Answering No hides an event's dates, which takes them out of the form; the stored dates must
  // still be cleared, or they keep pricing the loan where the loan officer can no longer see them.
  const clearsHiddenStoredDates =
    hasFinancesDeclarations(finances) && hasStoredCreditEventDates(storedFinances);

  if (!hasDateFields && !clearsHiddenStoredDates) {
    return null;
  }

  const visibleDateKeys = getVisibleCreditEventDateKeys({ finances, storedFinances });
  const fallbackOverridden = _.isEmpty(visibleDateKeys) ? false : null;

  return {
    ...Object.values(CreditEventDateKey).reduce((acc, key) => {
      acc[key] = visibleDateKeys.includes(key) ? toRequestDate(finances[key]) : null;

      return acc;
    }, {}),
    [CREDIT_EVENT_DATES_OVERRIDDEN_KEY]:
      finances?.[CREDIT_EVENT_DATES_OVERRIDDEN_KEY] ?? fallbackOverridden,
  };
};

/**
 * The modal's loaded borrower with only its credit-report event fields replaced from a fresh read,
 * so a refresh shows the new dates and events without touching anything the user is editing.
 */
export const getRefreshedCreditEventsEntityData = ({ borrowers, entityId, entityData }) => {
  const borrower = borrowers?.find(({ id }) => id === entityId);

  if (!borrower || !entityData) {
    return {};
  }

  return {
    [entityId]: {
      ...entityData,
      lastCreditReportReferenceNumber:
        borrower.lastCreditReportReferenceNumber ?? entityData.lastCreditReportReferenceNumber,
      declarations: {
        ...entityData.declarations,
        finances: {
          ...entityData.declarations?.finances,
          ..._.pick(borrower.declarations?.finances, CREDIT_REPORT_FINANCES_KEYS),
        },
      },
    },
  };
};

export const normalizeForm = ({ form, entityId }) => {
  const finances = form[entityId]?.declarations?.finances ?? {};
  const {
    guarantorOnAnyDebtOutsideTheLoan,
    outstandingJudgements,
    presentlyObligatedOnAnyLoanOrDebt,
    partyToLawSuite,
    propertyInLieuOfForeclosureInPastSevenYears,
    foreclosureSoldLessOutstandingBalanceWithinPastSevenYears,
    isLastMonthsForeclosure,
    isLastMonthsBankruptcy,
    bankruptTypes,
  } = finances;

  return {
    ...form,
    [entityId]: {
      ...form[entityId],
      declarations: {
        ...form[entityId]?.declarations,
        finances: {
          guarantorOnAnyDebtOutsideTheLoan,
          outstandingJudgements,
          presentlyObligatedOnAnyLoanOrDebt,
          partyToLawSuite,
          propertyInLieuOfForeclosureInPastSevenYears,
          foreclosureSoldLessOutstandingBalanceWithinPastSevenYears,
          isLastMonthsForeclosure,
          isLastMonthsBankruptcy,

          ...getConditionalObject(isLastMonthsBankruptcy, {
            bankruptTypes,
          }),
          ..._.pick(finances, CREDIT_EVENT_FORM_KEYS),
        },
      },
    },
  };
};
